import { TrendingUp, TrendingDown } from 'lucide-react';
import ChipTip from '@/components/ChipTip';

/**
 * RecentDividendEventsLine — standalone strips for Recent Hikes and
 * Recent Cuts in the watchlist.
 *
 * Round 105c (Jun 2026) — chip split: ticker opens the Stock Modal,
 * the %-pill opens a ChipTip click-popover (hover text now works on
 * phones) with an "Open TICKER →" link at the bottom.
 */
export default function RecentDividendEventsLine({ stocks = [], onStockClick }) {
  const hikes = (stocks || [])
    .filter((s) => Number(s?.recent_div_change_pct) > 0)
    .sort((a, b) => Number(b.recent_div_change_pct) - Number(a.recent_div_change_pct))
    .slice(0, 8);
  const cuts = (stocks || [])
    .filter((s) => Number(s?.recent_div_change_pct) < 0)
    .sort((a, b) => Number(a.recent_div_change_pct) - Number(b.recent_div_change_pct))
    .slice(0, 8);

  if (hikes.length === 0 && cuts.length === 0) return null;

  const renderChip = (s, kind) => {
    const pct = Number(s.recent_div_change_pct);
    const tip = kind === 'hike'
      ? `${s.ticker} raised its dividend ${pct.toFixed(1)}% — a recent hike (last 90 days). Raises usually signal management's confidence in cash flow.`
      : `${s.ticker} cut its dividend ${pct.toFixed(1)}% — a recent cut (last 90 days). Cuts often precede further weakness; review the position.`;
    const pillCls = kind === 'hike'
      ? 'bg-emerald-600 text-white border border-emerald-700'
      : 'bg-rose-600 text-white border border-rose-700';
    return (
      <span key={s.ticker} className="inline-flex items-center gap-1 px-1 py-[2px] rounded">
        <button
          type="button"
          onClick={() => onStockClick && onStockClick(s.ticker)}
          className="text-[11px] font-bold font-mono px-0.5 rounded hover:bg-muted transition-colors"
          data-testid={`recent-${kind}-${s.ticker}`}
          title={`Open ${s.ticker} in the Stock Modal`}
        >
          {s.ticker}
        </button>
        <ChipTip
          text={tip}
          ticker={s.ticker}
          onOpenTicker={onStockClick}
          testId={`recent-${kind}-pill-${s.ticker}`}
        >
          <span className={`text-[9px] font-bold px-1 py-[1px] rounded-sm whitespace-nowrap ${pillCls}`}>
            {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
          </span>
        </ChipTip>
      </span>
    );
  };

  return (
    <div className="my-3 space-y-2">
      {hikes.length > 0 && (
        <div
          className="px-3 py-2 rounded-lg border bg-card flex flex-wrap items-center gap-2"
          data-testid="watchlist-recent-hikes-line"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
            Recent Hikes
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {hikes.map((s) => renderChip(s, 'hike'))}
          </div>
        </div>
      )}
      {cuts.length > 0 && (
        <div
          className="px-3 py-2 rounded-lg border bg-card flex flex-wrap items-center gap-2"
          data-testid="watchlist-recent-cuts-line"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
            <TrendingDown className="w-3.5 h-3.5 text-rose-500" />
            Recent Cuts
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {cuts.map((s) => renderChip(s, 'cut'))}
          </div>
        </div>
      )}
    </div>
  );
}
